import React from 'react';

import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { PaymentModel } from '../../../../../shared/models/payment.model';

export interface SwitchGatewayModalProps {
  config: {
    showModal: boolean;
    isSwitching: boolean;
  };
  data: {
    payment: PaymentModel | null;
    targetGateway: PaymentGateway | null;
  };
  eventHandlers: {
    closeHandler: () => void;
    confirmHandler: () => void;
  };
}

function getGatewayLabel(gateway: PaymentGateway | null): string {
  return gateway === PaymentGateway.GEIDEA ? 'Geidea' : 'Paymob';
}

/**
 * functional component SwitchGatewayModal
 * @param {SwitchGatewayModalProps}
 */
export const SwitchGatewayModal: React.FC<SwitchGatewayModalProps> = ({
  config: { showModal, isSwitching },
  data: { payment, targetGateway },
  eventHandlers: { closeHandler, confirmHandler }
}) => {
  const isMobileView = useWindowResizer();

  const targetLabel = getGatewayLabel(targetGateway);
  const currentLabel = getGatewayLabel(payment?.paymentGateway || null);

  return (
    <Modal
      config={{ showModal, fullScreen: false, maxWidth: 'sm', showClose: !isSwitching }}
      eventHandlers={{
        closeModalHandler: () => {
          if (!isSwitching) {
            closeHandler();
          }
        }
      }}
    >
      <div className="flex flex-col gap-5 px-4 pt-2 pb-4 sm:px-10">
        <div>
          <h2 className="font-gilroy-bold text-3xl font-bold text-[#37454d]">
            Switch to {targetLabel}?
          </h2>
          <hr className="mt-3 border-gray-200" />
        </div>

        
        <p className="text-xl text-neutral-500">
          This cancels the current {currentLabel} payment and creates a new pending payment on{' '}
          {targetLabel}. Any payment link already sent to the guest will stop working.
        </p>

        {payment && (
          <div className="flex flex-col gap-2 rounded-xl bg-gray-100 p-4 text-base text-[#37454d]">
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Payment Id</span>
              <span className="truncate">{payment.id}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Amount</span>
              <span>
                {(payment.currency || 'EGP').toUpperCase()}{' '}
                {(payment.paymentAmount || 0).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse gap-3 px-4 pt-2 pb-6 sm:flex-row sm:justify-end sm:px-10">
        <Button
          disabled={isSwitching}
          onClick={closeHandler}
          type={ButtonType.primaryInverted}
          tracking={{ action: 'close', category: 'payments', label: 'switch-gateway-modal' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Cancel
        </Button>
        <Button
          disabled={isSwitching}
          loading={isSwitching}
          onClick={confirmHandler}
          type={ButtonType.default}
          tracking={{ action: 'submit', category: 'payments', label: 'switch-gateway-modal' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Switch to {targetLabel}
        </Button>
      </div>
    </Modal>
  );
};
